import { clsx } from 'clsx';
import type { ReactNode } from 'react';

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-muted border-line',
  brand: 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-800',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  warning: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  danger: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  info: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800',
};

export function Badge({ tone = 'neutral', children, className }: {
  tone?: Tone; children: ReactNode; className?: string;
}) {
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
      TONES[tone], className,
    )}>
      {children}
    </span>
  );
}

/** Maps a status string from the API to a sensible tone. */
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, Tone> = {
    active: 'success', paid: 'success', settled: 'success', success: 'success', ready: 'success',
    served: 'success', completed: 'success', confirmed: 'success', received: 'success',
    open: 'info', preparing: 'info', sent: 'info', new: 'info', draft: 'neutral',
    pending: 'warning', partial: 'warning', invited: 'warning', in_transit: 'warning',
    suspended: 'danger', voided: 'danger', failure: 'danger', denied: 'danger',
    cancelled: 'danger', overdue: 'danger', expired: 'danger',
  };
  const label = status.replace(/_/g, ' ');
  return <Badge tone={map[status] ?? 'neutral'}>{label}</Badge>;
}
